import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useImpersonationStore } from "../auth/useImpersonationStore";
import { useUser } from "../auth/useUser";
import { apiClient } from "../api/client";

// 0016 Slice 3 — sticky red banner shown on every page in the user
// app whenever the request authenticates via pmt_session_admin (the
// admin's impersonation cookie). The auth middleware stamps the
// X-Impersonating-User-ID + X-Impersonator-Admin-ID response headers;
// the apiClient response middleware (in api/client.ts) propagates
// them into useImpersonationStore.
//
// While impersonating, /me returns the TARGET user — so we can pull
// their email straight off useUser() rather than making a separate
// admin call.
export function ImpersonationBanner() {
  const { active, adminUID } = useImpersonationStore();
  const { data: user } = useUser();
  const qc = useQueryClient();
  const [stopping, setStopping] = useState(false);

  if (!active || !user) return null;

  const onStop = async () => {
    setStopping(true);
    try {
      await apiClient.POST("/api/v1/me/stop-impersonating", {
        params: { header: { "X-Requested-With": "xhr" } },
      });
      useImpersonationStore.getState().clear();
      // Re-fetch /me — after the impersonation cookie is cleared the
      // browser falls back to the regular session (the admin's own).
      await qc.invalidateQueries({ queryKey: ["me"] });
    } finally {
      setStopping(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 inset-x-0 z-50 bg-danger-solid text-white shadow-lg"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-10 flex items-center gap-3 text-sm">
        <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">
          impersonating
        </span>
        <span className="font-medium truncate">{user.email}</span>
        {adminUID && (
          <span className="font-mono text-[10px] opacity-70">
            as admin {adminUID.slice(0, 8)}…
          </span>
        )}
        <button
          type="button"
          onClick={onStop}
          disabled={stopping}
          className="ml-auto rounded-md bg-white/90 text-danger-solid px-3 py-1 text-xs font-medium hover:bg-white transition-colors disabled:opacity-50"
        >
          {stopping ? "stopping…" : "Stop"}
        </button>
      </div>
    </div>
  );
}
